import React, { useMemo } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Stack,
  Chip,
} from '@mui/material';
import { normalizeTimestamp } from './chartUtils';
import WidgetState from './WidgetState';

// A heartbeat sample counts as "up" (healthy) when its value is >= 1. This
// covers the common encodings: 0/1 up-down signals, received-heartbeat counts
// (>= 1 means at least one beat), and percentage-style availability
// (99.98 >= 1). Anything below 1 is treated as a down/error sample.
const isUpSample = (value) => Number.isFinite(value) && value >= 1;

const HeartbeatWidget = ({ data }) => {
  const statusInfo = useMemo(() => {
    // Collect the ACTUAL samples from whichever shape we were handed. No
    // fabrication: uptime, status and "last checked" are all derived from these.
    let samples = null; // array of { t: epochMs, v: number }

    if (data && data.data) {
      if (data.type === 'timeseries' && Array.isArray(data.data) && data.data.length > 0) {
        const series = data.data[0];
        if (series && Array.isArray(series.values) && series.values.length > 0) {
          samples = series.values
            .map(point => ({ t: normalizeTimestamp(point[0]), v: parseFloat(point[1]) }))
            .filter(s => Number.isFinite(s.v));
        }
      } else if (data.type === 'scalar') {
        const v = parseFloat(data.data);
        if (Number.isFinite(v)) {
          // A scalar carries no timestamp. Stamping Date.now() made the widget
          // render the RENDER time as "Last checked", i.e. a fabricated fact
          // about the data. Mark it unknown and let the UI omit the line.
          samples = [{ t: null, v }];
        }
      }
    }

    if (!samples || samples.length === 0) {
      return null; // honest empty state — no fabricated Down/uptime
    }

    // Uptime = fraction of healthy (non-error) samples across the real series.
    const upCount = samples.reduce((n, s) => n + (isUpSample(s.v) ? 1 : 0), 0);
    const uptime = (upCount / samples.length) * 100;

    // Newest sample by timestamp — drives both "last checked" and current status.
    const latest = samples.reduce((a, b) => (b.t >= a.t ? b : a), samples[0]);
    const latestUp = isUpSample(latest.v);

    // Status reflects the current sample plus any observed downtime in the window.
    let status;
    if (!latestUp) {
      status = 'error';   // currently down
    } else if (uptime < 99.9) {
      status = 'warning'; // up now, but had failures over the window
    } else {
      status = 'success'; // healthy throughout
    }

    const statusMap = {
      success: { color: '#52c41a', text: 'Healthy' },
      warning: { color: '#faad14', text: 'Degraded' },
      error: { color: '#f5222d', text: 'Down' },
    };

    return { ...statusMap[status], status, uptime, lastCheckedMs: latest.t };
  }, [data]);

  if (!statusInfo) {
    return <WidgetState isEmpty />;
  }

  return (
    <Box sx={{ textAlign: 'center', py: 2.5, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
      <Stack spacing={2} alignItems="center">
        {/* Status indicator dot */}
        <Box
          sx={{
            width: 16,
            height: 16,
            borderRadius: '50%',
            backgroundColor: statusInfo.color,
            boxShadow: `0 0 8px ${statusInfo.color}80`,
          }}
        />

        {/* Status text */}
        <Typography
          variant="h4"
          sx={{ color: statusInfo.color, fontWeight: 700 }}
        >
          {statusInfo.text}
        </Typography>

        {/* Circular uptime indicator */}
        <Box sx={{ position: 'relative', display: 'inline-flex' }}>
          <CircularProgress
            variant="determinate"
            value={statusInfo.uptime}
            size={100}
            thickness={6}
            sx={{
              color: statusInfo.status === 'error' ? 'error.main'
                : statusInfo.status === 'warning' ? 'warning.main'
                : 'success.main',
            }}
          />
          <Box
            sx={{
              top: 0,
              left: 0,
              bottom: 0,
              right: 0,
              position: 'absolute',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Typography variant="body1" sx={{ fontWeight: 700 }}>
              {statusInfo.uptime.toFixed(2)}%
            </Typography>
          </Box>
        </Box>

        {/* Additional info */}
        <Stack spacing={0.5} alignItems="center">
          {statusInfo.lastCheckedMs != null && (
            <Typography variant="body2" color="text.secondary">
              Last checked: {new Date(statusInfo.lastCheckedMs).toLocaleString()}
            </Typography>
          )}
          <Chip
            label={`Uptime: ${statusInfo.uptime.toFixed(2)}%`}
            size="small"
            color={statusInfo.status === 'success' ? 'success' : statusInfo.status === 'warning' ? 'warning' : 'error'}
            variant="outlined"
          />
        </Stack>
      </Stack>
    </Box>
  );
};

export default React.memo(HeartbeatWidget, (prevProps, nextProps) => {
  return prevProps.data === nextProps.data;
});
